import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

declare global {
    interface Window {
        Cashfree?: (options: {
            mode: "sandbox" | "production";
        }) => {
            checkout: (options: {
                paymentSessionId: string;
                redirectTarget?: "_self" | "_blank" | "_modal";
            }) => Promise<any>;
        };
    }
}

interface PaymentResponse {
    status: number;
    message: any;

    environment?: string;

    order_id?: string;

    order_amount?: number;
    order_currency?: string;

    payment_session_id?: string;

    customer_code?: string;

    payment_status?: string;

    // ============================================
    // PAYMENT RESPONSE DATA
    // ============================================

    payment_return?: number;

    customer_id?: string;
    customer_name?: string;

    mobile_no?: string;
    customer_phone?: string;

    email_id?: string;
    customer_email?: string;

    payment_date?: string;

    transaction_no?: string;

    payment_method?: string;

    bank_reference?: string;

    payment_message?: string;

    amount?: number;
}

const API_URL =
    "https://erp.futureinfosys.in/Expwwc";

const PaymentGateway: React.FC = () => {

    const [amount, setAmount] =
        useState<string>("100");

    const [customerCode, setCustomerCode] =
        useState<string>("678");

    const [customerName, setCustomerName] =
        useState<string>("EXPRESS CUSTOMER");

    const [customerEmail, setCustomerEmail] =
        useState<string>("test@example.com");

    const [customerPhone, setCustomerPhone] =
        useState<string>("9876543210");

    const [orderId, setOrderId] =
        useState<string>("");

    const [paymentStatus, setPaymentStatus] =
        useState<string>("");

    const [loading, setLoading] =
        useState<boolean>(false);

    const [sdkLoaded, setSdkLoaded] =
        useState<boolean>(false);

    const [receiptVisible, setReceiptVisible] = useState<boolean>(false);
    const [paymentDate, setPaymentDate] = useState<string>("");
    const [paidAmount, setPaidAmount] = useState<number>(0);
    const [receiptCustomerCode, setReceiptCustomerCode] = useState<string>("");
    const [receiptCustomerName, setReceiptCustomerName] = useState<string>("");
    const [receiptCustomerEmail, setReceiptCustomerEmail] = useState<string>("");
    const [receiptCustomerPhone, setReceiptCustomerPhone] = useState<string>("");
    const [receiptTransactionId, setReceiptTransactionId] = useState<string>("");


    // =====================================================
    // LOAD CASHFREE SDK
    // =====================================================

    useEffect(() => {

        if (window.Cashfree) {
            setSdkLoaded(true);
            return;
        }

        const existingScript =
            document.querySelector(
                'script[src="https://sdk.cashfree.com/js/v3/cashfree.js"]'
            );

        if (existingScript) {
            existingScript.addEventListener(
                "load",
                () => {
                    setSdkLoaded(true);
                }
            );
            return;
        }

        const script =
            document.createElement("script");

        script.src =
            "https://sdk.cashfree.com/js/v3/cashfree.js";

        script.async = true;

        script.onload = () => {
            console.log("Cashfree SDK Loaded");
            setSdkLoaded(true);
        };

        script.onerror = () => {
            console.error("Cashfree SDK Load Failed");
            setSdkLoaded(false);
        };

        document.body.appendChild(script);

    }, []);




    // =====================================================
    // HANDLE CASHFREE RETURN
    // =====================================================

    useEffect(() => {
        const handlePaymentReturn = async () => {
            const params = new URLSearchParams(window.location.search);
            const paymentReturn = params.get("payment_return");
            const returnedOrderId = params.get("order_id");

            if (paymentReturn !== "1" || !returnedOrderId) return;

            console.log("Cashfree returned with Order ID:", returnedOrderId);
            setOrderId(returnedOrderId);
            setPaymentStatus("VERIFYING");

            // Customer details entered before redirect are restored after the page reloads.
            try {
                const saved = sessionStorage.getItem("expwwc_payment_customer");
                if (saved) {
                    const c = JSON.parse(saved);
                    if (c.customerCode) setCustomerCode(c.customerCode);
                    if (c.customerName) setCustomerName(c.customerName);
                    if (c.customerEmail) setCustomerEmail(c.customerEmail);
                    if (c.customerPhone) setCustomerPhone(c.customerPhone);
                    if (c.amount) setAmount(String(c.amount));
                }
            } catch (e) {
                console.warn("Unable to restore payment customer details", e);
            }

            try {
                setLoading(true);

                // Cashfree can take a short moment to move the order to PAID.
                // Verify a few times before declaring it unsuccessful.
                let result: PaymentResponse | null = null;
                for (let attempt = 1; attempt <= 6; attempt++) {
                    const response = await axios.post<PaymentResponse>(
                        `${API_URL}/Payment/PaymentGateway`,
                        { action: "verify", order_id: returnedOrderId },
                        { headers: { "Content-Type": "application/json" }, timeout: 60000 }
                    );

                    result = response.data;
                    console.log(`Auto Verify Attempt ${attempt}:`, result);

                    if (result.status === 1 && result.payment_status === "PAID") break;

                    if (attempt < 6) {
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    }
                }

                if (result && result.status === 1 && result.payment_status === "PAID") {

                    // Show the receipt directly. Do NOT put a success popup in front of it.

                    showReceipt(
                        result,
                        Number(result.amount ?? result.order_amount ?? amount ?? 0)
                    );

                    sessionStorage.removeItem("expwwc_payment_customer");

                } else {
                    setPaymentStatus(result?.payment_status || "NOT PAID");
                    await Swal.fire({
                        icon: "info",
                        title: "Payment Status",
                        text: typeof result?.message === "string"
                            ? result.message
                            : "Payment is not completed yet. Please click Verify Payment."
                    });
                }
            } catch (error: any) {
                console.error("AUTO VERIFY PAYMENT ERROR:", error);
                setPaymentStatus("VERIFICATION FAILED");
                await Swal.fire({
                    icon: "error",
                    title: "Verification Error",
                    text: error?.response?.data?.message || error?.message || "Unable to verify payment."
                });
            } finally {
                setLoading(false);
                const cleanUrl = window.location.origin + window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);
            }
        };

        handlePaymentReturn();
    }, []);

    // =====================================================
    // RECEIPT ACTIONS
    // =====================================================

    const receiptHTML = () => `
        <div class="receipt">
            <h2 class="title">Payment Receipt</h2>
            <div class="success">✓ PAYMENT SUCCESSFUL</div>
            <div class="row"><span class="label">Order ID</span><span class="value">${orderId}</span></div>
            <div class="row"><span class="label">Paid Amount</span><span class="value">₹${paidAmount.toFixed(2)}</span></div>
            <div class="row"><span class="label">Customer Name</span><span class="value">${receiptCustomerName}</span></div>
            <div class="row"><span class="label">Customer Code</span><span class="value">${receiptCustomerCode}</span></div>
            <div class="row"><span class="label">Email</span><span class="value">${receiptCustomerEmail}</span></div>
            <div class="row"><span class="label">Mobile</span><span class="value">${receiptCustomerPhone}</span></div>
            <div class="row"><span class="label">Payment Status</span><span class="value">PAID</span></div>
            <div class="row"><span class="label">Payment Date</span><span class="value">${paymentDate}</span></div>
        </div>`;

    const openReceiptWindow = (autoPrint = false) => {
        const w = window.open("", "_blank");
        if (!w) { Swal.fire("Popup Blocked", "Please allow popups for the receipt.", "warning"); return; }
        w.document.write(`<!doctype html><html><head><title>Payment Receipt - ${orderId}</title><style>body{font-family:Arial,sans-serif;padding:30px;color:#172033}.receipt{max-width:700px;margin:auto;border:1px solid #ddd;border-radius:14px;padding:30px}.title{text-align:center;margin-bottom:25px}.success{text-align:center;color:#15803d;font-weight:bold;margin-bottom:25px}.row{display:flex;justify-content:space-between;gap:20px;padding:10px 0;border-bottom:1px solid #eee}.label{color:#64748b}.value{font-weight:700;text-align:right;word-break:break-word}@media print{body{padding:0}}</style></head><body>${receiptHTML()}${autoPrint ? '<script>window.onload=function(){window.print();}</script>' : ''}</body></html>`);
        w.document.close(); w.focus();
        if (autoPrint) setTimeout(() => w.print(), 300);
    };

    const downloadReceiptPDF = () => openReceiptWindow(true);
    const printReceipt = () => openReceiptWindow(true);

    const shareReceipt = async () => {
        const text = `Payment Successful\nOrder ID: ${orderId}\nPaid Amount: ₹${paidAmount.toFixed(2)}\nCustomer: ${receiptCustomerName}\nCustomer Code: ${receiptCustomerCode}\nEmail: ${receiptCustomerEmail}\nMobile: ${receiptCustomerPhone}\nPayment Status: PAID\nPayment Date: ${paymentDate}`;
        if (navigator.share) { try { await navigator.share({ title: "Payment Receipt", text }); } catch (e: any) { if (e?.name !== "AbortError") console.error(e); } }
        else { try { await navigator.clipboard.writeText(text); await Swal.fire("Receipt Copied", "Receipt details copied.", "info"); } catch { await Swal.fire("Share Not Supported", text, "info"); } }
    };

    const showReceipt = (
        result: PaymentResponse,
        fallbackAmount: number
    ) => {

        // ============================================
        // BACKEND JSON RESPONSE DIRECTLY
        // ============================================

        setPaidAmount(
            Number(
                result.amount ??
                result.order_amount ??
                fallbackAmount
            )
        );


        // ============================================
        // CUSTOMER CODE
        // ============================================

        setReceiptCustomerCode(
            result.customer_id ||
            result.customer_code ||
            customerCode.trim()
        );


        // ============================================
        // CUSTOMER NAME
        // ============================================

        setReceiptCustomerName(
            result.customer_name ||
            customerName.trim()
        );


        // ============================================
        // CUSTOMER EMAIL
        // ============================================

        setReceiptCustomerEmail(
            result.email_id ||
            result.customer_email ||
            customerEmail.trim()
        );


        // ============================================
        // CUSTOMER MOBILE
        // ============================================

        setReceiptCustomerPhone(
            result.mobile_no ||
            result.customer_phone ||
            customerPhone.trim()
        );


        // ============================================
        // TRANSACTION ID
        // ============================================

        setReceiptTransactionId(
            result.transaction_no ||
            ""
        );


        // ============================================
        // PAYMENT DATE
        // ============================================

        const paidAt =
            result.payment_date ||
            "";

        setPaymentDate(
            paidAt
                ? new Date(paidAt).toLocaleString(
                    "en-IN",
                    {
                        dateStyle: "medium",
                        timeStyle: "medium"
                    }
                )
                : new Date().toLocaleString(
                    "en-IN",
                    {
                        dateStyle: "medium",
                        timeStyle: "medium"
                    }
                )
        );


        // ============================================
        // PAYMENT STATUS
        // ============================================

        setPaymentStatus(
            result.payment_status ||
            "PAID"
        );


        // ============================================
        // SHOW RECEIPT
        // ============================================

        setReceiptVisible(true);


        setTimeout(() => {

            document
                .getElementById("payment-receipt")
                ?.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });

        }, 100);
    };

    // =====================================================
    // CREATE PAYMENT
    // =====================================================

    const createPayment = async () => {

        try {

            setLoading(true);

            setPaymentStatus("");

            // =============================================
            // VALIDATE AMOUNT
            // =============================================

            const numericAmount =
                Number(amount);

            if (
                !Number.isFinite(numericAmount) ||
                numericAmount <= 0
            ) {

                await Swal.fire(
                    "Invalid Amount",
                    "Please enter a valid amount.",
                    "warning"
                );

                return;
            }


            // =============================================
            // VALIDATE CUSTOMER CODE
            // =============================================

            if (
                !customerCode.trim()
            ) {

                await Swal.fire(
                    "Customer Code Required",
                    "Please enter customer code.",
                    "warning"
                );

                return;
            }


            // =============================================
            // VALIDATE PHONE
            // =============================================

            const phone =
                customerPhone
                    .replace(/\D/g, "");

            if (
                phone.length !== 10
            ) {

                await Swal.fire(
                    "Invalid Mobile",
                    "Please enter a valid 10 digit mobile number.",
                    "warning"
                );

                return;
            }


            // =============================================
            // CHECK SDK
            // =============================================

            if (!window.Cashfree) {

                await Swal.fire(
                    "Cashfree Loading",
                    "Please wait a moment and try again.",
                    "info"
                );

                return;
            }


            // =============================================
            // CREATE CASHFREE ORDER
            // =============================================

            // Preserve form data because Cashfree returns to a fresh page load.
            sessionStorage.setItem(
                "expwwc_payment_customer",
                JSON.stringify({
                    customerCode: customerCode.trim(),
                    customerName: customerName.trim(),
                    customerEmail: customerEmail.trim(),
                    customerPhone: phone,
                    amount: numericAmount
                })
            );

            console.log(
                "Creating Cashfree order..."
            );

            const response =
                await axios.post<PaymentResponse>(

                    `${API_URL}/Payment/PaymentGateway`,

                    {

                        action:
                            "create",

                        amount:
                            Number(
                                numericAmount.toFixed(2)
                            ),

                        customer_code:
                            customerCode.trim(),

                        customer_name:
                            customerName.trim(),

                        customer_email:
                            customerEmail.trim(),

                        customer_phone:
                            phone

                    },

                    {

                        headers: {

                            "Content-Type":
                                "application/json"

                        },

                        timeout:
                            60000

                    }

                );


            console.log(
                "Cashfree API Response:",
                response.data
            );


            const result =
                response.data;


            // =============================================
            // CHECK RESPONSE
            // =============================================

            if (
                result.status !== 1 ||
                !result.payment_session_id
            ) {

                throw new Error(

                    typeof result.message ===
                        "string"

                        ? result.message

                        : "Cashfree order creation failed."

                );

            }


            // =============================================
            // SAVE ORDER ID
            // =============================================

            setOrderId(
                result.order_id || ""
            );


            // =============================================
            // OPEN CASHFREE CHECKOUT
            // =============================================

            const cashfree =
                window.Cashfree({

                    mode:
                        "production"

                });


            console.log(
                "Opening Cashfree Checkout..."
            );


            await cashfree.checkout({

                paymentSessionId:
                    result.payment_session_id,

                redirectTarget:
                    "_self"

            });


        } catch (error: any) {

            console.error(
                "CREATE PAYMENT ERROR:",
                error
            );


            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Unable to create payment.";


            await Swal.fire(

                "Payment Error",

                typeof message === "string"

                    ? message

                    : "Unable to create payment.",

                "error"

            );

        } finally {

            setLoading(false);

        }

    };


    // =====================================================
    // VERIFY PAYMENT
    // =====================================================

    const verifyPayment = async () => {

        if (!orderId) {

            await Swal.fire(
                "Order ID Missing",
                "Please create payment first.",
                "warning"
            );

            return;
        }


        try {

            setLoading(true);


            const response =
                await axios.post<PaymentResponse>(

                    `${API_URL}/Payment/PaymentGateway`,

                    {

                        action:
                            "verify",

                        order_id:
                            orderId

                    },

                    {

                        headers: {

                            "Content-Type":
                                "application/json"

                        },

                        timeout:
                            60000

                    }

                );


            console.log(
                "Verify Response:",
                response.data
            );


            const result =
                response.data;


            if (
                result.status === 1 &&
                result.payment_status === "PAID"
            ) {

                showReceipt(
                    result,
                    Number(result.amount ?? result.order_amount ?? amount ?? 0)
                );

                await Swal.fire({

                    icon:
                        "success",

                    title:
                        "Payment Successful",

                    text:
                        "Payment has been successfully verified.",

                    confirmButtonText:
                        "OK"

                });

            } else {

                setPaymentStatus(

                    result.payment_status ||
                    "NOT PAID"

                );


                await Swal.fire({

                    icon:
                        "info",

                    title:
                        "Payment Status",

                    text:
                        typeof result.message ===
                            "string"

                            ? result.message

                            : "Payment is not completed."

                });

            }


        } catch (error: any) {

            console.error(
                "VERIFY PAYMENT ERROR:",
                error
            );


            await Swal.fire(

                "Verification Error",

                error?.response?.data?.message ||
                error?.message ||
                "Unable to verify payment.",

                "error"

            );

        } finally {

            setLoading(false);

        }

    };


    // =====================================================
    // UI
    // =====================================================

    return (

        <div className="payment-page">

            <style>{`

                * {
                    box-sizing: border-box;
                }

                .payment-page {

                    min-height: 100vh;

                    padding:
                        35px 20px;

                    background:
                        radial-gradient(
                            circle at top right,
                            rgba(37, 99, 235, 0.10),
                            transparent 35%
                        ),
                        #f5f7fb;

                    font-family:
                        Inter,
                        system-ui,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        sans-serif;

                    color:
                        #172033;
                }


                .payment-wrapper {

                    max-width:
                        1050px;

                    margin:
                        auto;
                }


                .payment-header {

                    background:
                        #ffffff;

                    border:
                        1px solid #e6eaf0;

                    border-radius:
                        18px;

                    padding:
                        18px 24px;

                    margin-bottom:
                        22px;

                    display:
                        flex;

                    align-items:
                        center;

                    justify-content:
                        space-between;

                    box-shadow:
                        0 8px 30px
                        rgba(
                            15,
                            23,
                            42,
                            0.06
                        );
                }


                .payment-logo-area {

                    display:
                        flex;

                    align-items:
                        center;

                    gap:
                        12px;
                }


                .payment-logo {

                    width:
                        45px;

                    height:
                        45px;

                    border-radius:
                        12px;

                    background:
                        #111827;

                    color:
                        #ffffff;

                    display:
                        flex;

                    align-items:
                        center;

                    justify-content:
                        center;

                    font-weight:
                        900;

                    font-size:
                        19px;
                }


                .payment-title {

                    margin:
                        0;

                    font-size:
                        17px;

                    font-weight:
                        800;
                }


                .payment-subtitle {

                    margin:
                        3px 0 0;

                    font-size:
                        11px;

                    color:
                        #64748b;
                }


                .live-status {

                    display:
                        flex;

                    align-items:
                        center;

                    gap:
                        7px;

                    font-size:
                        11px;

                    font-weight:
                        800;

                    color:
                        #15803d;
                }


                .live-dot {

                    width:
                        8px;

                    height:
                        8px;

                    border-radius:
                        50%;

                    background:
                        #16a34a;
                }


                .payment-grid {

                    display:
                        grid;

                    grid-template-columns:
                        0.95fr 1.05fr;

                    gap:
                        22px;
                }


                .payment-info,
                .payment-card {

                    background:
                        #ffffff;

                    border:
                        1px solid #e6eaf0;

                    border-radius:
                        22px;

                    box-shadow:
                        0 16px 45px
                        rgba(
                            15,
                            23,
                            42,
                            0.07
                        );
                }


                .payment-info {

                    padding:
                        38px;

                    min-height:
                        580px;

                    display:
                        flex;

                    flex-direction:
                        column;

                    justify-content:
                        space-between;
                }


                .eyebrow {

                    margin-bottom:
                        9px;

                    font-size:
                        11px;

                    font-weight:
                        800;

                    letter-spacing:
                        0.12em;

                    text-transform:
                        uppercase;

                    color:
                        #64748b;
                }


                .main-heading {

                    margin:
                        0 0 16px;

                    font-size:
                        38px;

                    line-height:
                        1.08;

                    letter-spacing:
                        -0.04em;
                }


                .description {

                    margin:
                        0;

                    color:
                        #64748b;

                    line-height:
                        1.7;

                    font-size:
                        14px;
                }


                .method-list {

                    display:
                        grid;

                    grid-template-columns:
                        1fr 1fr;

                    gap:
                        10px;

                    margin-top:
                        30px;
                }


                .method {

                    padding:
                        14px;

                    border:
                        1px solid #e8ebf0;

                    border-radius:
                        13px;

                    background:
                        #fafbfc;

                    font-size:
                        13px;

                    font-weight:
                        750;

                    color:
                        #334155;
                }


                .security-note {

                    display:
                        flex;

                    align-items:
                        center;

                    gap:
                        9px;

                    font-size:
                        12px;

                    color:
                        #64748b;
                }


                .security-icon {

                    width:
                        9px;

                    height:
                        9px;

                    border-radius:
                        50%;

                    background:
                        #16a34a;
                }


                .payment-card {

                    padding:
                        30px;
                }


                .card-top {

                    display:
                        flex;

                    justify-content:
                        space-between;

                    align-items:
                        flex-start;

                    margin-bottom:
                        22px;
                }


                .card-heading {

                    margin:
                        0;

                    font-size:
                        25px;

                    letter-spacing:
                        -0.025em;
                }


                .production-badge {

                    padding:
                        7px 10px;

                    border-radius:
                        999px;

                    background:
                        #fff7ed;

                    color:
                        #c2410c;

                    font-size:
                        10px;

                    font-weight:
                        900;
                }


                .amount-section {

                    padding:
                        19px;

                    border-radius:
                        16px;

                    background:
                        #f8fafc;

                    border:
                        1px solid #e6eaf0;

                    margin-bottom:
                        19px;
                }


                .amount-label {

                    margin-bottom:
                        6px;

                    font-size:
                        10px;

                    font-weight:
                        800;

                    color:
                        #64748b;

                    letter-spacing:
                        .08em;
                }


                .amount-row {

                    display:
                        flex;

                    align-items:
                        center;

                    gap:
                        7px;
                }


                .currency {

                    font-size:
                        25px;

                    font-weight:
                        900;
                }


                .amount-input {

                    width:
                        100%;

                    border:
                        0;

                    outline:
                        0;

                    background:
                        transparent;

                    font-size:
                        31px;

                    font-weight:
                        900;

                    color:
                        #111827;
                }


                .form-group {

                    margin-bottom:
                        15px;
                }


                .form-label {

                    display:
                        block;

                    margin-bottom:
                        7px;

                    font-size:
                        12px;

                    font-weight:
                        800;

                    color:
                        #475569;
                }


                .form-input {

                    width:
                        100%;

                    height:
                        46px;

                    border:
                        1px solid #dfe4eb;

                    border-radius:
                        11px;

                    padding:
                        0 13px;

                    outline:
                        0;

                    font-size:
                        14px;

                    color:
                        #172033;

                    transition:
                        .2s;
                }


                .form-input:focus {

                    border-color:
                        #64748b;

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            15,
                            23,
                            42,
                            .05
                        );
                }


                .pay-button {

                    width:
                        100%;

                    height:
                        53px;

                    margin-top:
                        8px;

                    border:
                        0;

                    border-radius:
                        12px;

                    background:
                        #111827;

                    color:
                        #ffffff;

                    font-size:
                        15px;

                    font-weight:
                        850;

                    cursor:
                        pointer;

                    transition:
                        transform .15s,
                        opacity .15s;
                }


                .pay-button:hover:not(:disabled) {

                    transform:
                        translateY(-1px);
                }


                .pay-button:disabled {

                    opacity:
                        .6;

                    cursor:
                        not-allowed;
                }


                .verify-button {

                    width:
                        100%;

                    height:
                        45px;

                    margin-top:
                        10px;

                    border:
                        1px solid #dfe4eb;

                    border-radius:
                        11px;

                    background:
                        #ffffff;

                    color:
                        #334155;

                    font-size:
                        13px;

                    font-weight:
                        800;

                    cursor:
                        pointer;
                }


                .order-box {

                    margin-top:
                        18px;

                    border-top:
                        1px solid #edf0f4;

                    padding-top:
                        16px;
                }


                .order-row {

                    display:
                        flex;

                    justify-content:
                        space-between;

                    gap:
                        15px;

                    padding:
                        6px 0;

                    font-size:
                        12px;
                }


                .order-row span:first-child {

                    color:
                        #64748b;
                }


                .order-row span:last-child {

                    font-weight:
                        800;

                    text-align:
                        right;

                    word-break:
                        break-all;
                }


                .sdk-status {

                    margin-top:
                        12px;

                    padding:
                        10px 12px;

                    border-radius:
                        10px;

                    background:
                        #f8fafc;

                    color:
                        #64748b;

                    font-size:
                        11px;

                    text-align:
                        center;
                }


                .receipt-section{grid-column:1 / -1;margin-top:0;background:#fff;border:1px solid #dfe4eb;border-radius:22px;padding:34px;box-shadow:0 16px 45px rgba(15,23,42,.07);position:relative;overflow:hidden}.receipt-section:before{content:"";position:absolute;left:0;top:0;right:0;height:5px;background:#15803d}.receipt-head{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;margin-bottom:24px}.receipt-brand{font-size:12px;font-weight:900;letter-spacing:.08em;text-transform:uppercase;color:#64748b}.receipt-main-title{margin:4px 0 0;font-size:30px;font-weight:950;color:#111827}.receipt-paid-badge{padding:8px 12px;border-radius:999px;background:#ecfdf5;color:#15803d;font-size:11px;font-weight:900;white-space:nowrap}.receipt-summary{display:grid;grid-template-columns:1.2fr .8fr;gap:18px;margin-bottom:22px}.receipt-amount-card{padding:22px;border-radius:16px;background:#f8fafc;border:1px solid #e6eaf0}.receipt-amount-label{font-size:10px;font-weight:900;letter-spacing:.08em;color:#64748b}.receipt-amount{font-size:34px;font-weight:950;margin-top:5px;color:#111827}.receipt-meta-card{padding:22px;border-radius:16px;background:#ecfdf5;border:1px solid #bbf7d0}.receipt-meta-label{font-size:10px;font-weight:900;letter-spacing:.08em;color:#166534}.receipt-meta-value{font-size:16px;font-weight:900;color:#166534;margin-top:6px}.receipt-section .receipt-title{display:none}.receipt-section .receipt-success{display:none}
                .receipt-title{text-align:center;margin:0 0 5px;font-size:24px;font-weight:900}.receipt-success{text-align:center;color:#15803d;font-weight:900;margin-bottom:22px}.receipt-row{display:flex;justify-content:space-between;gap:20px;padding:13px 0;border-bottom:1px solid #edf0f4;font-size:13px}.receipt-row span:first-child{font-weight:600}.receipt-row span:last-child{max-width:65%}.receipt-row span:first-child{color:#64748b}.receipt-row span:last-child{font-weight:800;text-align:right;word-break:break-word}.receipt-actions{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:20px}.receipt-action{min-height:44px;border:1px solid #dfe4eb;border-radius:11px;background:#fff;color:#172033;font-size:13px;font-weight:800;cursor:pointer}
                @media (max-width: 800px) {

                    .payment-grid {

                        grid-template-columns:
                            1fr;
                    }

                    .payment-info {

                        min-height:
                            auto;

                        padding:
                            28px;
                    }

                    .main-heading {

                        font-size:
                            30px;
                    }

                    .payment-header {

                        padding:
                            15px 18px;
                    }

                    .payment-card {

                        padding:
                            22px;
                    }

                }


                @media (max-width: 800px) {.receipt-summary{grid-template-columns:1fr}.receipt-head{align-items:flex-start}.receipt-actions{grid-template-columns:1fr 1fr}.receipt-section{padding:24px}}

                @media (max-width: 480px) {

                    .payment-page {

                        padding:
                            15px 10px;
                    }

                    .payment-header {

                        border-radius:
                            14px;
                    }

                    .payment-grid {

                        gap:
                            14px;
                    }

                    .payment-info,
                    .payment-card {

                        border-radius:
                            16px;
                    }

                    .receipt-head{flex-direction:column}.receipt-main-title{font-size:25px}.receipt-actions{grid-template-columns:1fr}.receipt-section{padding:20px}.receipt-row{align-items:flex-start}.receipt-row span:last-child{max-width:58%;text-align:right}.method-list {

                        grid-template-columns:
                            1fr;
                    }

                }

            `}</style>


            <div className="payment-wrapper">


                {/* ================================================= */}
                {/* HEADER */}
                {/* ================================================= */}

                <div className="payment-header">

                    <div className="payment-logo-area">

                        <div className="payment-logo">
                            PG
                        </div>

                        <div>

                            <h3 className="payment-title">
                                Your ERP
                            </h3>

                            <p className="payment-subtitle">
                                Secure Online Payment
                            </p>

                        </div>

                    </div>


                    <div className="live-status">

                        <span className="live-dot"></span>

                        Production

                    </div>

                </div>


                <div className="payment-grid">


                    {/* ================================================= */}
                    {/* LEFT INFO */}
                    {/* ================================================= */}

                    <div className="payment-info">

                        <div>

                            <div className="eyebrow">
                                Secure Checkout
                            </div>

                            <h1 className="main-heading">
                                Pay securely,
                                <br />
                                your way.
                            </h1>

                            <p className="description">

                                Complete your payment using
                                UPI, QR, Cards, Net Banking
                                or your preferred payment app.

                            </p>


                            <div className="method-list">

                                <div className="method">
                                    UPI
                                </div>

                                <div className="method">
                                    QR Code
                                </div>

                                <div className="method">
                                    Google Pay
                                </div>

                                <div className="method">
                                    PhonePe
                                </div>

                                <div className="method">
                                    Paytm
                                </div>

                                <div className="method">
                                    Debit / Credit Card
                                </div>

                                <div className="method">
                                    Net Banking
                                </div>

                                <div className="method">
                                    Other UPI Apps
                                </div>

                            </div>

                        </div>


                        <div className="security-note">

                            <span className="security-icon"></span>

                            Secure payment powered by Cashfree

                        </div>

                    </div>


                    {/* ================================================= */}
                    {/* PAYMENT CARD */}
                    {/* ================================================= */}

                    <div className="payment-card">


                        <div className="card-top">

                            <div>

                                <h2 className="card-heading">
                                    Make Payment
                                </h2>

                            </div>

                            <span className="production-badge">
                                LIVE
                            </span>

                        </div>


                        {/* ================================================= */}
                        {/* AMOUNT */}
                        {/* ================================================= */}

                        <div className="amount-section">

                            <div className="amount-label">
                                PAYMENT AMOUNT
                            </div>

                            <div className="amount-row">

                                <span className="currency">
                                    ₹
                                </span>

                                <input
                                    className="amount-input"
                                    type="number"
                                    min="1"
                                    step="0.01"
                                    value={amount}
                                    onChange={(e) =>
                                        setAmount(
                                            e.target.value
                                        )
                                    }
                                />

                            </div>

                        </div>


                        {/* ================================================= */}
                        {/* CUSTOMER CODE */}
                        {/* ================================================= */}

                        <div className="form-group">

                            <label className="form-label">
                                Customer Code
                            </label>

                            <input
                                className="form-input"
                                value={customerCode}
                                onChange={(e) =>
                                    setCustomerCode(
                                        e.target.value
                                    )
                                }
                                placeholder="Enter customer code"
                            />

                        </div>


                        {/* ================================================= */}
                        {/* CUSTOMER NAME */}
                        {/* ================================================= */}

                        <div className="form-group">

                            <label className="form-label">
                                Customer Name
                            </label>

                            <input
                                className="form-input"
                                value={customerName}
                                onChange={(e) =>
                                    setCustomerName(
                                        e.target.value
                                    )
                                }
                                placeholder="Enter customer name"
                            />

                        </div>


                        {/* ================================================= */}
                        {/* EMAIL */}
                        {/* ================================================= */}

                        <div className="form-group">

                            <label className="form-label">
                                Email
                            </label>

                            <input
                                className="form-input"
                                type="email"
                                value={customerEmail}
                                onChange={(e) =>
                                    setCustomerEmail(
                                        e.target.value
                                    )
                                }
                                placeholder="customer@example.com"
                            />

                        </div>


                        {/* ================================================= */}
                        {/* PHONE */}
                        {/* ================================================= */}

                        <div className="form-group">

                            <label className="form-label">
                                Mobile Number
                            </label>

                            <input
                                className="form-input"
                                type="tel"
                                maxLength={10}
                                value={customerPhone}
                                onChange={(e) =>
                                    setCustomerPhone(
                                        e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 10)
                                    )
                                }
                                placeholder="10 digit mobile number"
                            />

                        </div>


                        {/* ================================================= */}
                        {/* PAY BUTTON */}
                        {/* ================================================= */}

                        <button
                            type="button"
                            className="pay-button"
                            onClick={createPayment}
                            disabled={
                                loading ||
                                !sdkLoaded
                            }
                        >

                            {loading

                                ? "Opening Secure Checkout..."

                                : !sdkLoaded

                                    ? "Loading Payment..."

                                    : `Pay ₹${Number(
                                        amount || 0
                                    ).toFixed(2)}`

                            }

                        </button>


                        {/* ================================================= */}
                        {/* SDK STATUS */}
                        {/* ================================================= */}

                        <div className="sdk-status">

                            {sdkLoaded

                                ? "Cashfree Checkout Ready"

                                : "Loading Cashfree Secure Checkout..."

                            }

                        </div>


                        {/* ================================================= */}
                        {/* ORDER DETAILS */}
                        {/* ================================================= */}

                        {orderId && (

                            <div className="order-box">

                                <div className="order-row">

                                    <span>
                                        Order ID
                                    </span>

                                    <span>
                                        {orderId}
                                    </span>

                                </div>


                                {paymentStatus && (

                                    <div className="order-row">

                                        <span>
                                            Payment Status
                                        </span>

                                        <span>
                                            {paymentStatus}
                                        </span>

                                    </div>

                                )}


                                <button
                                    type="button"
                                    className="verify-button"
                                    onClick={
                                        verifyPayment
                                    }
                                    disabled={loading}
                                >

                                    Verify Payment

                                </button>

                            </div>

                        )}

                    </div>

                    {receiptVisible && paymentStatus === "PAID" && (
                        <div id="payment-receipt" className="receipt-section">
                            <div className="receipt-head">
                                <div>
                                    <div className="receipt-brand">Expwwc • Secure Payment</div>
                                    <h2 className="receipt-main-title">Payment Receipt</h2>
                                </div>
                                <div className="receipt-paid-badge">✓ PAYMENT SUCCESSFUL</div>
                            </div>

                            <div className="receipt-summary">
                                <div className="receipt-amount-card">
                                    <div className="receipt-amount-label">AMOUNT PAID</div>
                                    <div className="receipt-amount">₹{paidAmount.toFixed(2)}</div>
                                </div>
                                <div className="receipt-meta-card">
                                    <div className="receipt-meta-label">PAYMENT STATUS</div>
                                    <div className="receipt-meta-value">PAID</div>
                                </div>
                            </div>

                            <div className="receipt-row"><span>Order ID</span><span>{orderId}</span></div>
                            <div className="receipt-row"><span>Transaction ID</span><span>{receiptTransactionId || "Not available"}</span></div>
                            <div className="receipt-row"><span>Customer Name</span><span>{receiptCustomerName}</span></div>
                            <div className="receipt-row"><span>Customer Code</span><span>{receiptCustomerCode}</span></div>
                            <div className="receipt-row"><span>Email</span><span>{receiptCustomerEmail}</span></div>
                            <div className="receipt-row"><span>Mobile</span><span>{receiptCustomerPhone}</span></div>
                            <div className="receipt-row"><span>Payment Date</span><span>{paymentDate}</span></div>

                            <div className="receipt-actions">
                                <button type="button" className="receipt-action" onClick={downloadReceiptPDF}>📥 Download PDF</button>
                                <button type="button" className="receipt-action" onClick={printReceipt}>🖨️ Print / Save</button>
                                <button type="button" className="receipt-action" onClick={shareReceipt}>📤 Share</button>
                            </div>
                        </div>
                    )}

                </div>

            </div>

        </div>

    );

};

export default PaymentGateway;